import { useState, useEffect } from "react";
import { TrendingUp, Plus, User, Lock, Edit2, Trash2 } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { db } from "@/lib/supabase";
import { TablePagination } from "../ui/table-pagination";
import { buildInvestorRow, mapBackendToUI } from "./helper/mapperInversores";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { AdminInversoresEditModal } from "./AdminInversoresEditModal";

interface Contribucion {
  id: string;
  totalContribucion: number;
  fechaContribucion: string;
  porcentajeAdicional: number;
}

interface Inversor {
  id: string; // UUID from DB (users.id)
  user_id?: string;
  nombre: string;
  usuario: string;
  contrasena: string;
  capitalInvertido: number;
  fechaInversion: string;
  porcentajeCapitalSocial: number;
  contribuciones?: Contribucion[];
  totalContribuciones?: number;
  totalPorcentaje?: number;
  firstContributionDate?: string;
  link?: string | null;
}


export function AdminInversores() {
  const [inversores, setInversores] = useState<Inversor[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInversor, setEditingInversor] = useState<Inversor | null>(null);
  const [newInversor, setNewInversor] = useState<Partial<Inversor>>({});
  const [newContribucion, setNewContribucion] = useState({
    totalContribucion: "",
    fechaContribucion: "",
    porcentajeAdicional: "",
    nota: "",
    link: "",
  });
  const [editingContribucionId, setEditingContribucionId] = useState<
    string | null
  >(null);
  const [editingContribucionData, setEditingContribucionData] = useState({
    totalContribucion: "",
    fechaContribucion: "",
    porcentajeAdicional: "",
    nota: "",
    link: "",
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [inversorToDelete, setInversorToDelete] = useState<Inversor | null>(
    null
  );
  const [isSetPasswordOpen, setIsSetPasswordOpen] = useState(false);
  const [selectedForPassword, setSelectedForPassword] = useState<Inversor | null>(null);
  const [newPasswordValue, setNewPasswordValue] = useState("");
  const [confirmNewPasswordValue, setConfirmNewPasswordValue] = useState("");
  const [settingPassword, setSettingPassword] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loadingContribs, setLoadingContribs] = useState(false);

  // Load investors function (extracted so handlers can call it)
  async function loadInvestors() {
    setLoading(true);
    try {
      // 1) fetch investors paged (via db wrapper)
      const invRes = await db.getInvestorsPaged(page, pageSize);
      const users = invRes?.data || [];
      setTotal(Number(invRes?.total) || 0);


      // 2) fetch capital structure summary (per investor)
      const capitalRows = await db.getCapitalStructure(); // returns CapitalStructureRow[]

      // 3) fetch contributions in bulk (to populate firstContributionDate)
      const allContribs = await db.getAllContributions();


      // map capitalRows by investor_id
      const capitalMap = new Map(
        (capitalRows || []).map((r: any) => [r.investor_id, r])
      );

      // group contributions by investor
      const contribsByInvestor = new Map<string, any[]>();
      (allContribs || []).forEach((c: any) => {
        const arr = contribsByInvestor.get(c.investor_id) || [];
        arr.push(c);
        contribsByInvestor.set(c.investor_id, arr);
      });

      const uiRows: Inversor[] = (users || []).map((u: any) => {
        const cap = capitalMap.get(u.investor_id);
        // ensure we pass an array (may be empty) so mappers don't need to handle undefined
        const contribs = contribsByInvestor.get(u.investor_id) || [];
        const row = buildInvestorRow(u, cap, contribs);
        // ensure id is the user's uuid
        row.id = u.investor_id;
        row.user_id = u.id;
        return row;
      });


      setInversores(uiRows);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInvestors();
  }, [page, pageSize]);

  const handleCreateInversor = async () => {
    if (
      !newInversor.nombre ||
      !newInversor.usuario ||
      !newInversor.contrasena
    ) {
      alert(
        "Por favor completa los campos requeridos (Nombre, Usuario y Contraseña)"
      );
      return;
    }

    // Map UI fields to createInvestorCompleto payload
    // Require 'usuario' to be a valid email (must contain '@'); otherwise show error and abort
    const usuarioVal = String(newInversor.usuario || "");
    if (!usuarioVal.includes("@")) {
      alert(
        "El campo 'Usuario' debe ser una dirección de correo válida (contener '@')."
      );
      return;
    }
    const email = usuarioVal;

    const payload = {
      nombre: String(newInversor.nombre),
      email,
      password: String(newInversor.contrasena),
      capitalInvertido: Number(newInversor.capitalInvertido) || 0,
      fechaInversion: newInversor.fechaInversion || undefined,
      porcentajeCapitalSocial: Number(newInversor.porcentajeCapitalSocial) || 0,
      telefono: undefined,
      link: (newInversor as any).link || null,
    };

    try {
      setCreating(true);
      const res = await db.createInvestorCompleto(payload as any);

      if (!res || !res.success) {
        const msg = res?.error || "Error desconocido al crear inversor";
        alert(`No se pudo crear el inversor: ${msg}`);
        return;
      }

      // Use server-returned ids when available so local state matches backend
      const serverUserId = res.userId || (res as any).user?.id;
      const rpcData = res.rpcData || {};
      const serverInvestorId = rpcData?.investor_id || rpcData?.id || rpcData?.investor_profile_id;

      // Refresh from server to ensure we have canonical ids (user_id and investor id)
      await loadInvestors();
      setIsCreateModalOpen(false);
      setNewInversor({});
      alert(res.message || "Inversor creado correctamente");
    } catch (error: any) {
      console.error("Error creando inversor:", error);
      alert(error?.message || "Error al crear inversor");
    } finally {
      setCreating(false);
    }
  };

  const handleEditInversor = async () => {
    if (!editingInversor) return;

    try {
      setLoading(true);

      // split name into first and last
      const nombre = String(newInversor.nombre || editingInversor.nombre || "");
      const parts = nombre.trim().split(" ");
      const first_name = parts.shift() || nombre;
      const last_name = parts.join(" ") || first_name;

      // update user (email and names)
      await db.updateUser(editingInversor.user_id || editingInversor.id, {
        email: String(newInversor.usuario || editingInversor.usuario),
        first_name,
        last_name,
      });

      // reload list to reflect changes and recomputed totals
      await loadInvestors();

      setIsEditModalOpen(false);
      setNewInversor({});
      setEditingInversor(null);
    } catch (err) {
      console.error("Error updating investor", err);
      alert("Error actualizando inversor");
    } finally {
      setLoading(false);
    }
  };

  const handleAddContribucion = async () => {
    if (!editingInversor) return;

    try {
      setLoadingContribs(true);

      const investorId = editingInversor.id;
      const payload = {
        investor_id: investorId,
        amount: Number(newContribucion.totalContribucion) || 0,
        capital_percentage: Number(newContribucion.porcentajeAdicional) || 0,
        contribution_date:
          newContribucion.fechaContribucion ||
          new Date().toISOString().slice(0, 10),
        note: newContribucion.nota || null,
        link: newContribucion.link || null,
      };

      const created = await db.addContribution(payload as any);

      if (!created) throw new Error("No se creó la contribución");

      const newContribUI = mapBackendToUI(created as any);

      toast.success("Contribución añadida");

      // Update editingInversor locally so modal updates immediately
      setEditingInversor((prev) => {
        if (!prev) return prev;
        const updatedContribs = [...(prev.contribuciones || []), newContribUI];
        return {
          ...prev,
          contribuciones: updatedContribs,
          capitalInvertido: (prev.capitalInvertido || 0) + newContribUI.totalContribucion,
          porcentajeCapitalSocial: (prev.porcentajeCapitalSocial || 0) + newContribUI.porcentajeAdicional,
        };
      });

      // Update table list (inversores) to reflect new totals
      setInversores((prev) => prev.map((inv) => {
        if (inv.id !== investorId) return inv;
        return {
          ...inv,
          contribuciones: [...(inv.contribuciones || []), newContribUI],
          capitalInvertido: (inv.capitalInvertido || 0) + newContribUI.totalContribucion,
          porcentajeCapitalSocial: (inv.porcentajeCapitalSocial || 0) + newContribUI.porcentajeAdicional,
        };
      }));

      // Reset inputs
      setNewContribucion({
        totalContribucion: "",
        fechaContribucion: "",
        porcentajeAdicional: "",
        nota: "",
        link: "",
      });
    } catch (err) {
      console.error("Error adding contribution", err);
      toast.error("Error creando contribución");
    } finally {
      setLoadingContribs(false);
    }
  };

  const handleEditContribucion = async () => {
    if (!editingInversor || !editingContribucionId) return;

    try {
      setLoadingContribs(true);

      const payload = {
        contribution_id: editingContribucionId,
        amount: Number(editingContribucionData.totalContribucion) || 0,
        capital_percentage:
          Number(editingContribucionData.porcentajeAdicional) || 0,
        note: editingContribucionData.nota || null,
        link: editingContribucionData.link || null,
      };

      const updated = await db.updateContribution(payload as any);
      if (!updated) throw new Error("No se actualizó la contribución");

      const updatedUI = mapBackendToUI(updated as any);

      // compute deltas using previous editingInversor state
      const oldContrib = editingInversor.contribuciones?.find(c => c.id === editingContribucionId);
      const deltaAmount = (updatedUI.totalContribucion || 0) - (oldContrib?.totalContribucion || 0);
      const deltaPercentage = (updatedUI.porcentajeAdicional || 0) - (oldContrib?.porcentajeAdicional || 0);

      // update editingInversor
      setEditingInversor((prev) => {
        if (!prev) return prev;
        const updatedContribs = (prev.contribuciones || []).map(c => c.id === updatedUI.id ? updatedUI : c);
        return {
          ...prev,
          contribuciones: updatedContribs,
          capitalInvertido: (prev.capitalInvertido || 0) + deltaAmount,
          porcentajeCapitalSocial: (prev.porcentajeCapitalSocial || 0) + deltaPercentage,
        };
      });

      // update table list
      setInversores((prev) => prev.map(inv => {
        if (inv.id !== editingInversor.id) return inv;
        return {
          ...inv,
          contribuciones: (inv.contribuciones || []).map(c => c.id === updatedUI.id ? updatedUI : c),
          capitalInvertido: (inv.capitalInvertido || 0) + deltaAmount,
          porcentajeCapitalSocial: (inv.porcentajeCapitalSocial || 0) + deltaPercentage,
        };
      }));

      setEditingContribucionId(null);
      setEditingContribucionData({
        totalContribucion: "",
        fechaContribucion: "",
        porcentajeAdicional: "",
        nota: "",
        link: "",
      });
      toast.success("Contribución actualizada");
    } catch (err) {
      console.error("Error editing contribution", err);
      toast.error("Error actualizando contribución");
    } finally {
      setLoadingContribs(false);
    }
  };

  const handleDeleteContribucion = async (contribucionId: string) => {
    if (!editingInversor) return;

    try {
      setLoadingContribs(true);

      // find contrib to remove for delta
      const contribToRemove = editingInversor.contribuciones?.find(c => c.id === contribucionId);
      await db.deleteContribution(contribucionId);

      // update editingInversor
      setEditingInversor((prev) => {
        if (!prev) return prev;
        const updatedContribs = (prev.contribuciones || []).filter(c => c.id !== contribucionId);
        return {
          ...prev,
          contribuciones: updatedContribs,
          capitalInvertido: (prev.capitalInvertido || 0) - (contribToRemove?.totalContribucion || 0),
          porcentajeCapitalSocial: (prev.porcentajeCapitalSocial || 0) - (contribToRemove?.porcentajeAdicional || 0),
        };
      });

      // update table list
      setInversores((prev) => prev.map(inv => {
        if (inv.id !== editingInversor.id) return inv;
        return {
          ...inv,
          contribuciones: (inv.contribuciones || []).filter(c => c.id !== contribucionId),
          capitalInvertido: (inv.capitalInvertido || 0) - (contribToRemove?.totalContribucion || 0),
          porcentajeCapitalSocial: (inv.porcentajeCapitalSocial || 0) - (contribToRemove?.porcentajeAdicional || 0),
        };
      }));
      toast.success("Contribución eliminada");
    } catch (err) {
      console.error("Error deleting contribution", err);
      toast.error("Error eliminando contribución");
    } finally {
      setLoadingContribs(false);
    }
  };

  const handleDeleteInversor = async () => {
    if (!inversorToDelete) return;

    try {
      setDeleting(true);
      setLoading(true);

      // Prefer user_id if available (some rows use users.id vs investor_profiles.id)
      const targetId = inversorToDelete.user_id || inversorToDelete.id;

      // Call server-side deletion which removes DB rows and the auth user via edge function
      await db.deleteInvestor(targetId);

      // Refresh list to reflect server state (safer than local-only removal)
      await loadInvestors();

      setIsDeleteDialogOpen(false);
      setInversorToDelete(null);
    } catch (err: any) {
      console.error("Error deleting investor", err);
      alert(err?.message || "Error eliminando inversor");
    } finally {
      setLoading(false);
      setDeleting(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    if (!isFinite(value)) return "-";

    // Remove all rounding: truncate to two decimals (no rounding) and display two decimals
    const truncated = Math.trunc(value * 100) / 100;
    return `${truncated.toFixed(2)}%`;
  };

  const totalCapital = inversores.reduce(
    (acc, inv) => acc + inv.capitalInvertido,
    0
  );

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl text-[#0F172A] mb-2"
            style={{ fontWeight: 600 }}
          >
            Inversores
          </h1>
          <p className="text-gray-600">
            Gestión completa de socios e inversores del portfolio
          </p>
        </div>
        <Button variant="black" onClick={() => setIsCreateModalOpen(true)}>
          <Plus size={18} className="mr-2" />
          Nuevo inversor
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-6">
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm min-w-max">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total inversores</p>
                <p className="text-3xl" style={{ fontWeight: 600 }}>
                  {inversores.length}
                </p>
              </div>
              <div className="w-14 h-14 bg-[#2B59FF]/10 rounded-full flex items-center justify-center">
                <TrendingUp size={20} className="text-[#2B59FF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm min-w-max">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  Capital total invertido
                </p>
                <p className="text-3xl" style={{ fontWeight: 600 }}>
                  {formatCurrency(totalCapital)}
                </p>
              </div>
              <div className="w-14 h-14 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                <TrendingUp size={20} className="text-[#10B981]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/*  <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Filtros
          </Button>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Exportar
          </Button> */}
        </div>
      </div>

      {/* Investors Table */}
      <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="overflow-x-auto w-full rounded-lg bg-white">
          <style>{`
            /* Use internal horizontal scrolling: table grows to content width but never forces page overflow */
            .inv-table { width: max-content; min-width: 100%; table-layout: auto; }
            .inv-colgroup col { width: auto; }
            .inv-table th, .inv-table td { white-space: nowrap; }
            /* allow wrapping on very narrow screens */
            @media (max-width: 420px) { .inv-table th, .inv-table td { white-space: normal; } }
            /* ensure hover/background covers cells when scrolled */
            .inv-table tbody tr:hover td { background-color: rgba(249,250,251,1); }
          `}</style>
          <table className="table-auto divide-y divide-gray-200 inv-table">
            <colgroup className="inv-colgroup">
              <col className="col-index" />
              <col className="col-name" />
              <col className="col-capital" />
              <col className="col-user" />
              <col className="col-fecha" />
              <col className="col-percent" />
              <col className="col-actions" />
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 bg-white">
                  <th
                    className="text-left px-3 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600, width: "3%" }}
                  >
                  #
                </th>
                <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                  style={{ fontWeight: 600 }}
                >
                  Nombre
                </th>
                <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                  style={{ fontWeight: 600 }}
                >
                  Capital Invertido
                </th>
                <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                  style={{ fontWeight: 600 }}
                >
                  Usuario
                </th>
                {/* contraseña column removed */}
                  <th
                    className="text-left px-6 py-4 text-xs text-gray-600 uppercase tracking-wider"
                    style={{ fontWeight: 600 }}
                  >
                    Fecha Inversión
                  </th>
                <th
                    className="text-center px-4 py-4 text-xs text-gray-600 uppercase tracking-wider"
                  style={{ fontWeight: 600 }}
                >
                  % Capital Social
                </th>
                <th
                    className="text-center px-4 py-4 text-xs text-gray-600 uppercase tracking-wider"
                  style={{ fontWeight: 600 }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading
                ? Array.from({ length: pageSize }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="animate-pulse">
                        <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-6" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-8 bg-gray-200 rounded w-40" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-32" />
                      </td>
                        <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-5 bg-gray-200 rounded w-20" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="h-6 bg-gray-200 rounded w-16" />
                      </td>
                    </tr>
                  ))
                : inversores.map((inversor, index) => (
                    <tr
                      key={inversor.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                        <td className="px-3 py-4 w-12 text-sm text-gray-600 text-center">
                        {index + 1}
                      </td>
                        <td className="px-3 md:px-6 py-4 min-w-0">
                        <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10 bg-[#0F172A] text-white">
                            <AvatarFallback
                              className="bg-[#0F172A] text-white text-sm font-semibold"
                              style={{ backgroundColor: "#0F172A", color: "#FFFFFF" }}
                            >
                              {inversor.nombre
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .substring(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                            <span
                              className="text-sm text-[#0F172A] block"
                              style={{ fontWeight: 500 }}
                            >
                            {inversor.nombre}
                          </span>
                        </div>
                      </td>
                        <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right md:text-left">
                          <span
                            className="text-sm text-gray-900 block"
                            style={{ fontWeight: 500 }}
                          >
                            {formatCurrency(inversor.capitalInvertido)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="max-w-[180px]">
                            <span className="text-sm text-gray-700 block">
                              {inversor.usuario}
                            </span>
                          </div>
                        </td>
                      {/* contraseña column removed */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-700">
                            {inversor.fechaInversion
                              ? new Date(inversor.fechaInversion).toLocaleDateString("es-ES")
                              : "-"}
                          </span>
                        </td>
                      <td className="px-4 py-4 text-center whitespace-nowrap">
                        <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-black">
                          <span className="text-sm font-semibold text-white">
                            {formatPercentage(inversor.porcentajeCapitalSocial)}
                          </span>
                        </div>
                      </td>
                      <td className="text-center px-3 md:px-4 py-3 md:py-4 whitespace-nowrap">
                        <div className="inline-flex items-center gap-2 justify-center mx-auto">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-600 hover:text-[#2B59FF] hover:bg-[#2B59FF]/10"
                            onClick={async () => {
                              try {
                                setLoadingContribs(true);
                                const investorId = inversor.id || inversor.user_id || "";
                                const contribs = await db.getContributionsByInvestor(investorId);
                                const contribsUI = (contribs || []).map(mapBackendToUI);

                                setEditingInversor({ ...inversor, contribuciones: contribsUI });

                                setNewInversor({
                                  nombre: inversor.nombre,
                                  usuario: inversor.usuario,
                                  contrasena: inversor.contrasena,
                                  capitalInvertido: inversor.capitalInvertido,
                                  fechaInversion: inversor.fechaInversion,
                                  porcentajeCapitalSocial: inversor.porcentajeCapitalSocial,
                                });

                                setIsEditModalOpen(true);
                              } catch (err) {
                                console.error("Error loading contributions for investor", err);
                                alert("Error cargando contribuciones del inversor");
                              } finally {
                                setLoadingContribs(false);
                              }
                            }}
                          >
                            <Edit2 size={18} />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-600 hover:text-[#FF5757] hover:bg-[#FF5757]/10"
                            onClick={() => {
                              setInversorToDelete(inversor);
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-gray-600 hover:text-[#10B981] hover:bg-[#10B981]/10"
                            onClick={() => {
                              setSelectedForPassword(inversor);
                              setNewPasswordValue("");
                              setConfirmNewPasswordValue("");
                              setIsSetPasswordOpen(true);
                            }}
                          >
                            <Lock size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              {/* Totals Row */}
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-3 py-4 w-12 text-sm text-gray-600 text-center"></td>
                <td className="px-3 md:px-6 py-4 min-w-0">
                  <span className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    TOTAL
                  </span>
                </td>
                <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right md:text-left">
                  <span className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatCurrency(totalCapital)}
                  </span>
                </td>
                <td className="px-6 py-4"></td>
                <td className="px-6 py-4"></td>
                <td className="px-4 py-4 text-center whitespace-nowrap">
                  <span className="text-sm text-[#0F172A]" style={{ fontWeight: 600 }}>
                    {formatPercentage(
                      inversores.reduce((acc, inv) => acc + inv.porcentajeCapitalSocial, 0)
                    )}
                  </span>
                </td>
                <td className="text-center px-3 md:px-4 py-3 md:py-4 whitespace-nowrap"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex justify-end mt-4">
        <TablePagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          pageSize={pageSize}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => p + 1)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="w-full max-w-[720px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
          <DialogHeader className="pb-4">
            <DialogTitle className="text-2xl" style={{ fontWeight: 600 }}>Crear nuevo inversor</DialogTitle>
            <p className="text-sm text-gray-600 mt-2">Complete los datos del inversor</p>
          </DialogHeader>
            {/* Status area shown while creating */}
            {creating ? (
              <div className="flex items-center gap-3 p-4">
                <Spinner size={18} />
                <div>
                  <p className="text-sm font-medium text-gray-900">Creando inversor…</p>
                  <p className="text-sm text-gray-600">Se está creando el inversor. Esto puede tardar unos segundos.</p>
                </div>
              </div>
            ) : null}

            <div className="space-y-6 py-6 px-6">
            {/* Row 1 - Información personal */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Información personal
              </h3>
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="nombre" className="text-sm block">Nombre completo *</Label>
                  <Input
                    id="nombre"
                    placeholder="Nombre y apellidos del inversor"
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newInversor.nombre || ""}
                    onChange={(e) =>
                      setNewInversor({ ...newInversor, nombre: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 2 - Credenciales de acceso */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Credenciales de acceso
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="usuario" className="text-sm block">Usuario *</Label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      id="usuario"
                      placeholder="Nombre de usuario para acceso"
                      className="mt-2 h-10 rounded-full bg-white border-0 pl-10 px-4 shadow-sm"
                      value={newInversor.usuario || ""}
                      onChange={(e) =>
                        setNewInversor({
                          ...newInversor,
                          usuario: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  <Label htmlFor="contrasena" className="text-sm block">Contraseña *</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      id="contrasena"
                      type="password"
                      placeholder="Contraseña de acceso"
                      className="mt-2 h-10 rounded-full bg-white border-0 pl-10 px-4 shadow-sm"
                      value={newInversor.contrasena || ""}
                      onChange={(e) =>
                        setNewInversor({
                          ...newInversor,
                          contrasena: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Row 3 - Datos de inversión */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Datos de inversión
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="capitalInvertido" className="text-sm block">Capital Invertido (€)</Label>
                  <Input
                    id="capitalInvertido"
                    type="number"
                    placeholder="0"
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newInversor.capitalInvertido || ""}
                    onChange={(e) =>
                      setNewInversor({
                        ...newInversor,
                        capitalInvertido: Number(e.target.value),
                      })
                    }
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="fechaInversion" className="text-sm">
                    Fecha de Inversión
                  </Label>
                  <Input
                    id="fechaInversion"
                    type="date"
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newInversor.fechaInversion || ""}
                    onChange={(e) =>
                      setNewInversor({
                        ...newInversor,
                        fechaInversion: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="space-y-3">
                  <Label htmlFor="porcentajeCapitalSocial" className="text-sm">
                    % Capital Social
                  </Label>
                  <Input
                    id="porcentajeCapitalSocial"
                    type="number"
                    step="0.1"
                    placeholder="0.0"
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={newInversor.porcentajeCapitalSocial || ""}
                    onChange={(e) =>
                      setNewInversor({
                        ...newInversor,
                        porcentajeCapitalSocial: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Row 4 - Enlaces (opcional) */}
            <div>
              <h3
                className="text-lg text-[#0F172A] mb-4"
                style={{ fontWeight: 600 }}
              >
                Enlaces
              </h3>
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-3">
                  <Label htmlFor="link" className="text-sm">
                    Documento (URL)
                  </Label>
                  <Input
                    id="link"
                    type="url"
                    placeholder="https://... (opcional)"
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                    value={(newInversor as any).link || ""}
                    onChange={(e) =>
                      setNewInversor({ ...newInversor, link: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-6 border-t border-gray-100 flex justify-between items-center">
            <div>
              <Button
                variant="ghost"
                className="h-10 px-4"
                onClick={() => {
                  if (creating) return;
                  setIsCreateModalOpen(false);
                  setNewInversor({});
                }}
                disabled={creating}
              >
                Cancelar
              </Button>
            </div>
            <div>
              <Button
                variant="black"
                className="h-12 px-6 rounded-full"
                onClick={handleCreateInversor}
                disabled={creating}
              >
                {creating ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={14} /> Creando…
                  </span>
                ) : (
                  "Guardar inversor"
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <AdminInversoresEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setNewInversor({});
          setEditingInversor(null);
        }}
        editingInversor={editingInversor}
        newInversor={newInversor}
        setNewInversor={setNewInversor}
        handleEditInversor={handleEditInversor}
        newContribucion={newContribucion}
        setNewContribucion={setNewContribucion}
        handleAddContribucion={handleAddContribucion}
        editingContribucionId={editingContribucionId}
        setEditingContribucionId={setEditingContribucionId}
        editingContribucionData={editingContribucionData}
        setEditingContribucionData={setEditingContribucionData}
        handleEditContribucion={handleEditContribucion}
        handleDeleteContribucion={handleDeleteContribucion}
        loadingContribs={loadingContribs}
        formatCurrency={formatCurrency}
        formatPercentage={formatPercentage}
      />

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="w-full max-w-[500px] max-h-[90vh] overflow-y-auto bg-white text-gray-900 border border-gray-200 shadow-lg">
          <DialogHeader className="pb-6 border-b">
            <DialogTitle
              className="text-3xl text-[#0F172A]"
              style={{ fontWeight: 600 }}
            >
              Eliminar inversor
            </DialogTitle>
            <p className="text-gray-600 mt-2">
              ¿Estás seguro de que quieres eliminar este inversor?
            </p>
          </DialogHeader>
          {/* Status area shown while deleting */}
          {deleting ? (
            <div className="flex items-center gap-3 p-4">
              <Spinner size={18} />
              <div>
                <p className="text-sm font-medium text-gray-900">Procesando eliminación…</p>
                <p className="text-sm text-gray-600">Se está eliminando el inversor y sus datos. Esto puede tardar unos segundos.</p>
              </div>
            </div>
          ) : null}

          <DialogFooter className="pt-6 gap-3">
            <Button
              variant="outline"
              className="h-12 px-8"
              onClick={() => {
                if (deleting) return;
                setIsDeleteDialogOpen(false);
                setInversorToDelete(null);
              }}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              variant="black"
              className="h-12 px-8"
              onClick={handleDeleteInversor}
              disabled={deleting}
            >
              {deleting ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner size={14} /> Procesando…
                </span>
              ) : (
                "Eliminar inversor"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog open={isSetPasswordOpen} onOpenChange={setIsSetPasswordOpen}>
        <DialogContent className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
          <DialogHeader className="pb-6">
            <DialogTitle className="text-3xl text-[#0F172A]" style={{ fontWeight: 600 }}>
              Establecer nueva contraseña
            </DialogTitle>
            <p className="text-gray-600 mt-2">Define una nueva contraseña para el usuario seleccionado.</p>
          </DialogHeader>

          <div className="py-4 px-2 space-y-4">
            <div>
              <Label className="text-sm">Usuario</Label>
              <Input className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm" value={selectedForPassword?.usuario || ""} readOnly />
            </div>

            <div>
              <Label className="text-sm">Nueva contraseña</Label>
              <Input
                type="password"
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                value={newPasswordValue}
                onChange={(e) => setNewPasswordValue(e.target.value)}
              />
            </div>

            <div>
              <Label className="text-sm">Confirmar nueva contraseña</Label>
              <Input
                type="password"
                className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm"
                value={confirmNewPasswordValue}
                onChange={(e) => setConfirmNewPasswordValue(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="pt-6 border-t gap-3">
            <Button
              variant="outline"
              className="h-12 px-8"
              onClick={() => {
                if (settingPassword) return;
                setIsSetPasswordOpen(false);
                setSelectedForPassword(null);
              }}
              disabled={settingPassword}
            >
              Cancelar
            </Button>
            <Button
              variant="black"
              className="h-12 px-8"
              onClick={async () => {
                if (!selectedForPassword) return;
                if (newPasswordValue.length < 6) {
                  toast.error("La contraseña debe tener al menos 6 caracteres");
                  return;
                }
                if (newPasswordValue !== confirmNewPasswordValue) {
                  toast.error("Las contraseñas no coinciden");
                  return;
                }

                try {
                  setSettingPassword(true);

                  const userId = selectedForPassword.user_id || selectedForPassword.id;
                  await db.adminChangePassword(userId, newPasswordValue);

                  toast.success("Contraseña actualizada correctamente");
                  // refresh list to reflect potential changes
                  await loadInvestors();
                  setIsSetPasswordOpen(false);
                  setSelectedForPassword(null);
                } catch (err: any) {
                  console.error("Error setting password", err);
                  toast.error(err?.message || "Error cambiando la contraseña");
                } finally {
                  setSettingPassword(false);
                }
              }}
              disabled={settingPassword}
            >
              {settingPassword ? "Procesando…" : "Guardar contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
